import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

const ThemeContext = createContext(null);

// source: "default" (no saved preference) | "saved" (restored from localStorage) | "manual" (clicked this session)
function readInitialTheme() {
    try {
        const saved = localStorage.getItem("theme");
        if (saved === "dark" || saved === "light") return { theme: saved, source: "saved" };
        return { theme: "light", source: "default" };   // product rule: light by default regardless of OS scheme
    } catch {
        return { theme: "light", source: "default" };
    }
}

export function ThemeProvider({ children }) {
    const [{ theme, source }, setState] = useState(readInitialTheme);
    const sourceRef = useRef(source);
    sourceRef.current = source;

    useEffect(() => {
        const root = document.documentElement;
        const isDark = theme === "dark";
        if (isDark) root.classList.add("dark");
        else root.classList.remove("dark");
        root.style.backgroundColor = isDark ? "#111A1D" : "#F7F6F2";
        // Keep the browser chrome (address bar) in sync with the theme
        try {
            const meta = document.getElementById("theme-color-meta");
            if (meta) meta.setAttribute("content", isDark ? "#142E35" : "#F1F5F1");
        } catch {}
        try { localStorage.setItem("theme", theme); } catch {}
    }, [theme]);

    // Manual toggle only: restoring a saved preference on load is NOT a change and emits nothing.
    const toggle = useCallback(() => {
        setState((s) => {
            const next = s.theme === "dark" ? "light" : "dark";
            try { window.dispatchEvent(new CustomEvent("theme:manual-change", { detail: { mode: next } })); } catch {}
            return { theme: next, source: "manual" };
        });
    }, []);
    const setTheme = useCallback((t) => setState((s) => ({ theme: t, source: t === s.theme ? s.source : "manual" })), []);

    return <ThemeContext.Provider value={{ theme, source, toggle, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    return useContext(ThemeContext) || { theme: "light", source: "default", toggle: () => {}, setTheme: () => {} };
}
